import { formatHour } from "@/lib/api";
import { Lock, Wrench } from "lucide-react";

/**
 * Grille 24h de créneaux.
 * slots: [{hour, label, status: 'libre'|'reserve'|'indisponible'}]
 */
export const ScheduleGrid = ({ slots = [], selectedHours = [], onSlotClick, date }) => {
  const now = new Date();
  const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(
    now.getDate()
  ).padStart(2, "0")}`;
  const isToday = date === todayStr;
  const currentHour = now.getHours();

  return (
    <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 lg:grid-cols-6" data-testid="schedule-grid">
      {slots.map((slot) => {
        const isPast = isToday && slot.hour <= currentHour;
        const isBooked = slot.status === "reserve";
        const isBlocked = slot.status === "indisponible";
        const isSelected = selectedHours.includes(slot.hour);
        const isDisabled = isBooked || isBlocked || isPast;

        let stateClasses =
          "bg-[hsl(var(--studio-yellow)/0.10)] border-[hsl(var(--studio-yellow)/0.35)] hover:border-[hsl(var(--studio-yellow)/0.7)] hover:bg-[hsl(var(--studio-yellow)/0.18)] cursor-pointer";
        if (isSelected) {
          stateClasses =
            "bg-[hsl(var(--studio-yellow))] border-[hsl(var(--studio-yellow))] text-[hsl(var(--studio-black))] cursor-pointer";
        } else if (isBooked) {
          stateClasses =
            "bg-[hsl(var(--studio-ink))] border-border text-muted-foreground cursor-not-allowed opacity-80";
        } else if (isBlocked) {
          stateClasses =
            "bg-secondary border-dashed border-border text-muted-foreground cursor-not-allowed opacity-70";
        } else if (isPast) {
          stateClasses = "bg-secondary border-border text-muted-foreground cursor-not-allowed opacity-40";
        }

        return (
          <button
            key={slot.hour}
            type="button"
            disabled={isDisabled}
            onClick={() => !isDisabled && onSlotClick && onSlotClick(slot.hour)}
            className={`group relative flex flex-col items-start rounded-md border px-3 py-2 text-left text-sm tabular-nums transition-colors duration-150 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[hsl(var(--studio-yellow)/0.25)] focus-visible:ring-offset-2 focus-visible:ring-offset-background ${stateClasses}`}
            data-testid={`schedule-slot-${String(slot.hour).padStart(2, "0")}-00`}
          >
            <span className="font-mono-nums font-medium">{formatHour(slot.hour)}</span>
            <span className="mt-0.5 flex items-center gap-1 text-[11px] font-medium uppercase tracking-wide">
              {isBooked ? (
                <>
                  <Lock className="h-3 w-3" /> Réservé
                </>
              ) : isBlocked ? (
                <>
                  <Wrench className="h-3 w-3" /> Indispo.
                </>
              ) : isPast ? (
                "Passé"
              ) : isSelected ? (
                "Sélectionné"
              ) : (
                "Disponible"
              )}
            </span>
          </button>
        );
      })}
    </div>
  );
};

export const ScheduleLegend = () => (
  <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground" data-testid="schedule-legend">
    <span className="flex items-center gap-1.5">
      <span className="h-3 w-3 rounded-sm border border-[hsl(var(--studio-yellow)/0.35)] bg-[hsl(var(--studio-yellow)/0.10)]" />
      Disponible
    </span>
    <span className="flex items-center gap-1.5">
      <span className="h-3 w-3 rounded-sm bg-[hsl(var(--studio-yellow))]" />
      Sélectionné
    </span>
    <span className="flex items-center gap-1.5">
      <span className="h-3 w-3 rounded-sm border border-border bg-[hsl(var(--studio-ink))]" />
      Réservé
    </span>
    <span className="flex items-center gap-1.5">
      <span className="h-3 w-3 rounded-sm border border-dashed border-border bg-secondary" />
      Indisponible
    </span>
  </div>
);
